import React, { useState, useEffect, useMemo } from 'react';
import { 
  ChevronDown, ChevronUp, TrendingDown, TrendingUp, 
  DollarSign, Package, Users, Truck, ArrowLeftRight, 
  Landmark, BarChart3, AlertCircle, Info, Minus, 
  Layers, Activity, RefreshCw, Shield
} from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import { scenariosApi } from '../../api/scenarios';

/**
 * ScenarioImpactBanner Component
 * 
 * Smart Default Behavior:
 * - Active/Base scenario: No comparison by default (clean editing experience)
 * - Alternative scenarios: Auto-compare against Active baseline
 * 
 * Semantics:
 * - Cost: Negative delta = cost reduction = favorable
 * - Volume: Positive delta = volume increase = favorable
 * - Revenue: Positive delta = revenue increase = favorable
 */
const ScenarioImpactBanner = ({ 
  partNumber, 
  currentScenarioName = 'Active',
  currentScenarioId = null,
  programId = null,
  onBaselineChange = null,
  hasUnsavedChanges = false,
  lastSaveTime = null
}) => {
  // State
  const [isExpanded, setIsExpanded] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [scenarios, setScenarios] = useState([]);
  const [baselineScenarioId, setBaselineScenarioId] = useState(null);
  const [costImpactData, setCostImpactData] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [isViewingActiveScenario, setIsViewingActiveScenario] = useState(false);
  const [userManuallyChangedBaseline, setUserManuallyChangedBaseline] = useState(false);

  // Auto-refresh when data is saved
  useEffect(() => {
    if (lastSaveTime) {
      setRefreshKey(prev => prev + 1);
    }
  }, [lastSaveTime]);

  // Load available scenarios and apply smart default logic
  useEffect(() => {
    const loadScenarios = async () => {
      try {
        const result = await scenariosApi.getScenarios(partNumber, { programId });
        if (result.success && result.data) {
          setScenarios(result.data);
          
          // Find current scenario and active scenario
          const currentScenario = result.data.find(s => 
            currentScenarioId ? s.id === currentScenarioId : s.name === currentScenarioName
          );
          const activeScenario = result.data.find(s => s.is_active);
          
          // Determine if user is viewing the active/base scenario
          const viewingActive = currentScenario?.is_active || 
            (currentScenarioName === 'Active' && !currentScenarioId);
          setIsViewingActiveScenario(viewingActive);
          
          // SMART DEFAULT LOGIC (only if user hasn't manually changed baseline)
          if (!userManuallyChangedBaseline) {
            if (viewingActive) {
              // User is in Active/Base scenario - NO comparison by default
              setBaselineScenarioId(null);
            } else {
              // User is in Alternative scenario - Compare against Active
              if (activeScenario) {
                setBaselineScenarioId(activeScenario.id);
              } else {
                // No active scenario exists - leave null
                setBaselineScenarioId(null);
              }
            }
          }
        }
      } catch (err) {
        console.error('Failed to load scenarios:', err);
      }
    };

    if (partNumber) {
      // Reset manual override when scenario changes
      setUserManuallyChangedBaseline(false);
      loadScenarios();
    }
  }, [partNumber, currentScenarioName, currentScenarioId, programId]);

  // Load cost impact data when baseline changes
  useEffect(() => {
    const loadCostImpact = async () => {
      // Skip API call if no baseline selected
      if (!partNumber || !baselineScenarioId) {
        setLoading(false);
        setCostImpactData(null);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const options = {
          baselineScenarioId: baselineScenarioId
        };
        
        if (currentScenarioId) {
          options.currentScenarioId = currentScenarioId;
        } else {
          options.currentScenarioName = currentScenarioName;
        }
        if (programId) {
          options.programId = programId;
        }

        const result = await scenariosApi.getCostImpact(partNumber, options);
        
        if (result.success && result.data) {
          setCostImpactData(result.data);
        } else {
          setError(result.error || 'Failed to load cost impact data');
        }
      } catch (err) {
        console.error('Failed to load cost impact:', err);
        setError(err.message || 'Failed to load cost impact data');
      } finally {
        setLoading(false);
      }
    };

    loadCostImpact();
  }, [partNumber, currentScenarioName, currentScenarioId, baselineScenarioId, refreshKey, programId]);

  // Manual refresh handler
  const handleRefresh = () => {
    setRefreshKey(prev => prev + 1);
  };

  // Handle baseline change (user manual selection)
  const handleBaselineChange = (scenarioId) => {
    setUserManuallyChangedBaseline(true);
    setBaselineScenarioId(scenarioId === 'none' ? null : scenarioId);
    if (onBaselineChange) {
      onBaselineChange(scenarioId === 'none' ? null : scenarioId);
    }
  };

  // Format currency
  const formatCurrency = (cents, showSign = false) => {
    if (cents === null || cents === undefined) return '-';
    const dollars = cents / 100;
    const sign = showSign ? (dollars > 0 ? '+' : '') : '';
    if (Math.abs(dollars) >= 1000000) return `${sign}$${(dollars / 1000000).toFixed(1)}M`;
    if (Math.abs(dollars) >= 1000) return `${sign}$${(dollars / 1000).toFixed(0)}K`;
    return `${sign}$${dollars.toFixed(2)}`;
  };

  // Format number with commas
  const formatNumber = (num, showSign = false) => {
    if (num === null || num === undefined) return '-';
    const sign = showSign ? (num > 0 ? '+' : '') : '';
    if (Math.abs(num) >= 1000000) return `${sign}${(num / 1000000).toFixed(1)}M`;
    if (Math.abs(num) >= 1000) return `${sign}${(num / 1000).toFixed(0)}K`;
    return `${sign}${num.toLocaleString()}`;
  };

  // Format delta with sign and color (for costs - negative is good)
  const formatCostDelta = (cents) => {
    if (cents === null || cents === undefined) return { text: '-', color: 'text-gray-500' };
    
    const isFavorable = cents < 0;
    const color = cents === 0 ? 'text-gray-500' : (isFavorable ? 'text-green-600' : 'text-red-600');
    const sign = cents > 0 ? '+' : '';
    
    const dollars = cents / 100;
    if (Math.abs(dollars) >= 1000000) {
      return { text: `${sign}$${(dollars / 1000000).toFixed(1)}M`, color };
    }
    if (Math.abs(dollars) >= 1000) {
      return { text: `${sign}$${(dollars / 1000).toFixed(0)}K`, color };
    }
    return { text: `${sign}$${dollars.toFixed(2)}`, color };
  };

  // Format delta for volume/revenue (positive is good)
  const formatPositiveDelta = (value, isVolume = false) => {
    if (value === null || value === undefined) return { text: '-', color: 'text-gray-500' };
    
    const isFavorable = value > 0;
    const color = value === 0 ? 'text-gray-500' : (isFavorable ? 'text-green-600' : 'text-red-600');
    const sign = value > 0 ? '+' : '';
    
    if (isVolume) {
      if (Math.abs(value) >= 1000000) return { text: `${sign}${(value / 1000000).toFixed(1)}M`, color };
      if (Math.abs(value) >= 1000) return { text: `${sign}${(value / 1000).toFixed(0)}K`, color };
      return { text: `${sign}${value.toLocaleString()}`, color };
    } else {
      if (Math.abs(value) >= 1000000) return { text: `${sign}$${(value / 1000000).toFixed(1)}M`, color };
      if (Math.abs(value) >= 1000) return { text: `${sign}$${(value / 1000).toFixed(0)}K`, color };
      return { text: `${sign}$${value.toFixed(0)}`, color };
    }
  };

  // Get baseline scenario options (exclude current scenario)
  const baselineOptions = useMemo(() => {
    return scenarios.filter(s => 
      currentScenarioId ? s.id !== currentScenarioId : s.name !== currentScenarioName
    );
  }, [scenarios, currentScenarioName, currentScenarioId]);

  // Cost component metrics
  const costMetrics = useMemo(() => {
    if (!costImpactData?.has_comparison || !costImpactData?.component_impacts) {
      return [];
    }

    const { component_impacts } = costImpactData;
    
    const laborImpact = component_impacts?.find(c => c.component_type === 'labor_index') || { delta_cents: 0 };
    const materialImpact = component_impacts?.filter(c => ['base_material', 'material_index'].includes(c.component_type))
      .reduce((sum, c) => sum + (c.delta_cents || 0), 0);
    const logisticsImpact = component_impacts?.find(c => c.component_type === 'logistics') || { delta_cents: 0 };
    const tariffImpact = component_impacts?.find(c => c.component_type === 'tariff') || { delta_cents: 0 };
    const fxImpact = component_impacts?.find(c => c.component_type === 'fx_impact') || { delta_cents: 0 };

    return [
      { label: 'Labor', icon: Users, delta: laborImpact.delta_cents || 0, color: 'purple' },
      { label: 'Material', icon: Package, delta: materialImpact, color: 'blue' },
      { label: 'Logistics', icon: Truck, delta: logisticsImpact.delta_cents || 0, color: 'green' },
      { label: 'Tariff', icon: Landmark, delta: tariffImpact.delta_cents || 0, color: 'red' },
      { label: 'FX Impact', icon: ArrowLeftRight, delta: fxImpact.delta_cents || 0, color: 'yellow', isPlaceholder: true }
    ];
  }, [costImpactData]);

  // If no scenarios available to compare, show minimal banner
  if (baselineOptions.length === 0 && !loading) {
    return (
      <Card className="mb-4 border-gray-200 bg-gray-50">
        <CardContent className="p-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-gray-500">
              <Info className="h-4 w-4" />
              <span className="text-sm">Create additional scenarios to enable impact comparison</span>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Determine if comparison is active
  const hasActiveComparison = baselineScenarioId && costImpactData?.has_comparison;

  return (
    <TooltipProvider>
      <Card className={`mb-4 transition-all duration-200 ${
        !hasActiveComparison
          ? 'border-gray-200 bg-gray-50'
          : costImpactData?.summary?.is_favorable 
            ? 'border-green-200 bg-gradient-to-r from-green-50 to-emerald-50' 
            : costImpactData?.summary?.net_delta_cents > 0 
              ? 'border-red-200 bg-gradient-to-r from-red-50 to-orange-50'
              : 'border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50'
      }`}>
        <CardContent className="p-0">
          {/* Header Row */}
          <div 
            className="flex items-center justify-between p-3 cursor-pointer hover:bg-white/30 transition-colors"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            <div className="flex items-center gap-3">
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-6 w-6 p-0"
                onClick={(e) => { e.stopPropagation(); setIsExpanded(!isExpanded); }}
              >
                {isExpanded ? (
                  <ChevronUp className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
              </Button>
              
              <div className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-blue-600" />
                <span className="font-semibold text-gray-900">Scenario Impact</span>
              </div>

              {/* Active Scenario Indicator */}
              {isViewingActiveScenario && !baselineScenarioId && (
                <div className="flex items-center gap-1.5 ml-2 px-2 py-0.5 bg-blue-50 border border-blue-200 rounded text-blue-700">
                  <Shield className="h-3.5 w-3.5" />
                  <span className="text-xs font-medium">Baseline Scenario</span>
                </div>
              )}

              {/* Compact Summary when Collapsed */}
              {!isExpanded && hasActiveComparison && costImpactData?.summary && (
                <div className="flex items-center gap-3 ml-3">
                  <span className="text-sm text-gray-500">
                    vs {costImpactData.baseline_scenario?.name}
                  </span>
                  <Badge 
                    variant="outline" 
                    className={`${
                      costImpactData.summary.is_favorable 
                        ? 'bg-green-50 text-green-700 border-green-300' 
                        : costImpactData.summary.net_delta_cents > 0 
                          ? 'bg-red-50 text-red-700 border-red-300'
                          : 'bg-gray-50 text-gray-700 border-gray-300'
                    }`}
                  >
                    <DollarSign className="h-3 w-3 mr-1" />
                    {formatCostDelta(costImpactData.summary.net_delta_cents).text}
                  </Badge>
                  {costImpactData.summary.volume_delta !== 0 && (
                    <Badge 
                      variant="outline" 
                      className={`${
                        costImpactData.summary.volume_is_favorable 
                          ? 'bg-green-50 text-green-700 border-green-300' 
                          : 'bg-red-50 text-red-700 border-red-300'
                      }`}
                    >
                      <Layers className="h-3 w-3 mr-1" />
                      {formatPositiveDelta(costImpactData.summary.volume_delta, true).text}
                    </Badge>
                  )}
                </div>
              )}
            </div>

            {/* Controls */}
            <div className="flex items-center gap-3" onClick={(e) => e.stopPropagation()}>
              {/* Unsaved Changes Warning */}
              {hasUnsavedChanges && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center gap-1.5 px-2 py-1 bg-amber-50 border border-amber-200 rounded text-amber-700">
                      <AlertCircle className="h-3.5 w-3.5" />
                      <span className="text-xs font-medium">Unsaved changes</span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    Save your changes to update the comparison
                  </TooltipContent>
                </Tooltip>
              )}
              
              {/* Refresh Button */}
              {baselineScenarioId && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="h-8 w-8 p-0"
                      onClick={handleRefresh}
                      disabled={loading}
                    >
                      <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Refresh comparison</TooltipContent>
                </Tooltip>
              )}
              
              {/* Baseline Selector */}
              <span className="text-sm text-gray-500">Baseline:</span>
              <Select 
                value={baselineScenarioId || 'none'} 
                onValueChange={handleBaselineChange}
              >
                <SelectTrigger className="w-[180px] h-8 bg-white text-sm">
                  <SelectValue placeholder="Select..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">
                    <span className="text-gray-500">No comparison</span>
                  </SelectItem>
                  {baselineOptions.map((scenario) => (
                    <SelectItem key={scenario.id} value={scenario.id}>
                      <div className="flex items-center gap-2">
                        {scenario.is_active && (
                          <Badge variant="outline" className="text-xs h-4 px-1 bg-blue-50 text-blue-700 border-blue-200">Active</Badge>
                        )}
                        <span>{scenario.name}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Expanded Content */}
          {isExpanded && (
            <div className="border-t border-gray-200">
              {loading ? (
                <div className="p-4">
                  <div className="animate-pulse flex space-x-4">
                    <div className="flex-1 space-y-2">
                      <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                      <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                    </div>
                  </div>
                </div>
              ) : error ? (
                <div className="p-4 flex items-center gap-2 text-red-600">
                  <AlertCircle className="h-4 w-4" />
                  <span className="text-sm">{error}</span>
                </div>
              ) : !baselineScenarioId ? (
                // No comparison selected - show informational message
                <div className="p-6 flex flex-col items-center justify-center text-center">
                  <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-3">
                    <BarChart3 className="h-6 w-6 text-gray-400" />
                  </div>
                  <p className="text-sm text-gray-600 mb-1">
                    {isViewingActiveScenario 
                      ? 'You are viewing the active baseline scenario'
                      : 'No baseline scenario selected'
                    }
                  </p>
                  <p className="text-xs text-gray-400">
                    Select a baseline from the dropdown to compare cost, volume, and revenue impact
                  </p>
                </div>
              ) : !costImpactData?.has_comparison ? (
                <div className="p-4 flex items-center gap-2 text-gray-500">
                  <Info className="h-4 w-4" />
                  <span className="text-sm">Unable to load comparison data</span>
                </div>
              ) : (
                <>
                  {/* Cost Component Metrics Row */}
                  <div className="grid grid-cols-5 gap-3 p-4 bg-white/50">
                    {costMetrics.map((metric, index) => {
                      const IconComponent = metric.icon;
                      const delta = formatCostDelta(metric.delta);
                      
                      return (
                        <Tooltip key={index}>
                          <TooltipTrigger asChild>
                            <div className={`flex flex-col items-center p-2 rounded-lg border ${
                              metric.isPlaceholder 
                                ? 'bg-gray-50 border-gray-200 opacity-60' 
                                : 'bg-white border-gray-200 hover:border-gray-300'
                            } transition-colors`}>
                              <div className={`flex items-center gap-1 mb-1 text-${metric.color}-600`}>
                                <IconComponent className="h-3.5 w-3.5" />
                                <span className="text-xs font-medium">{metric.label}</span>
                              </div>
                              <div className={`text-base font-semibold ${delta.color}`}>
                                {metric.isPlaceholder ? (
                                  <span className="text-gray-400">N/A</span>
                                ) : (
                                  delta.text
                                )}
                              </div>
                            </div>
                          </TooltipTrigger>
                          <TooltipContent>
                            {metric.isPlaceholder 
                              ? 'FX Impact is not included in this comparison'
                              : `${metric.label} cost ${metric.delta < 0 ? 'reduction' : metric.delta > 0 ? 'increase' : 'unchanged'} per unit`
                            }
                          </TooltipContent>
                        </Tooltip>
                      );
                    })}
                  </div>

                  {/* Summary Footer with Cost, Volume, Revenue */}
                  <div className="grid grid-cols-4 gap-4 p-4 bg-gray-50/80 border-t border-gray-200">
                    {/* Net Cost Impact */}
                    <div className="flex flex-col">
                      <div className="flex items-center gap-1 text-xs text-gray-500 uppercase tracking-wide mb-1">
                        <DollarSign className="h-3 w-3" />
                        Net Cost Impact
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`text-xl font-bold ${
                          costImpactData.summary.is_favorable 
                            ? 'text-green-600' 
                            : costImpactData.summary.net_delta_cents > 0 
                              ? 'text-red-600' 
                              : 'text-gray-600'
                        }`}>
                          {formatCostDelta(costImpactData.summary.net_delta_cents).text}
                        </span>
                        <Badge 
                          variant="outline" 
                          className={`text-xs ${
                            costImpactData.summary.is_favorable 
                              ? 'bg-green-50 text-green-700' 
                              : costImpactData.summary.net_delta_cents > 0 
                                ? 'bg-red-50 text-red-700' 
                                : 'bg-gray-50 text-gray-700'
                          }`}
                        >
                          {costImpactData.summary.net_delta_percent > 0 ? '+' : ''}{costImpactData.summary.net_delta_percent}%
                        </Badge>
                      </div>
                      <div className="text-xs text-gray-500 mt-1">per unit</div>
                    </div>

                    {/* Volume Impact */}
                    <div className="flex flex-col">
                      <div className="flex items-center gap-1 text-xs text-gray-500 uppercase tracking-wide mb-1">
                        <Layers className="h-3 w-3" />
                        Volume Impact
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`text-xl font-bold ${
                          costImpactData.summary.volume_is_favorable 
                            ? 'text-green-600' 
                            : costImpactData.summary.volume_delta < 0 
                              ? 'text-red-600' 
                              : 'text-gray-600'
                        }`}>
                          {formatPositiveDelta(costImpactData.summary.volume_delta, true).text}
                        </span>
                        {costImpactData.summary.volume_delta_percent !== 0 && (
                          <Badge 
                            variant="outline" 
                            className={`text-xs ${
                              costImpactData.summary.volume_is_favorable 
                                ? 'bg-green-50 text-green-700' 
                                : costImpactData.summary.volume_delta < 0
                                  ? 'bg-red-50 text-red-700' 
                                  : 'bg-gray-50 text-gray-700'
                            }`}
                          >
                            {costImpactData.summary.volume_delta_percent > 0 ? '+' : ''}{costImpactData.summary.volume_delta_percent}%
                          </Badge>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        total units ({formatNumber(costImpactData.summary.current_total_volume)} vs {formatNumber(costImpactData.summary.baseline_total_volume)})
                      </div>
                    </div>

                    {/* Revenue Impact */}
                    <div className="flex flex-col">
                      <div className="flex items-center gap-1 text-xs text-gray-500 uppercase tracking-wide mb-1">
                        <Activity className="h-3 w-3" />
                        Revenue Impact
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`text-xl font-bold ${
                          costImpactData.summary.revenue_is_favorable 
                            ? 'text-green-600' 
                            : costImpactData.summary.revenue_delta < 0 
                              ? 'text-red-600' 
                              : 'text-gray-600'
                        }`}>
                          {formatPositiveDelta(costImpactData.summary.revenue_delta).text}
                        </span>
                        {costImpactData.summary.revenue_delta_percent !== 0 && (
                          <Badge 
                            variant="outline" 
                            className={`text-xs ${
                              costImpactData.summary.revenue_is_favorable 
                                ? 'bg-green-50 text-green-700' 
                                : costImpactData.summary.revenue_delta < 0
                                  ? 'bg-red-50 text-red-700' 
                                  : 'bg-gray-50 text-gray-700'
                            }`}
                          >
                            {costImpactData.summary.revenue_delta_percent > 0 ? '+' : ''}{costImpactData.summary.revenue_delta_percent}%
                          </Badge>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">total revenue</div>
                    </div>

                    {/* Status Indicator */}
                    <div className="flex flex-col justify-center items-end">
                      <div className={`flex items-center gap-2 px-4 py-2 rounded-lg ${
                        costImpactData.summary.is_favorable 
                          ? 'bg-green-100 text-green-800' 
                          : costImpactData.summary.net_delta_cents > 0 
                            ? 'bg-red-100 text-red-800' 
                            : 'bg-gray-100 text-gray-800'
                      }`}>
                        {costImpactData.summary.is_favorable ? (
                          <>
                            <TrendingDown className="h-5 w-5" />
                            <span className="font-medium">Cost Favorable</span>
                          </>
                        ) : costImpactData.summary.net_delta_cents > 0 ? (
                          <>
                            <TrendingUp className="h-5 w-5" />
                            <span className="font-medium">Cost Increase</span>
                          </>
                        ) : (
                          <>
                            <Minus className="h-5 w-5" />
                            <span className="font-medium">No Change</span>
                          </>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 mt-2">
                        {costImpactData.summary.annual_savings_cents > 0 
                          ? `Saves ${formatCurrency(costImpactData.summary.annual_savings_cents)}/year`
                          : costImpactData.summary.annual_savings_cents < 0
                            ? `Costs ${formatCurrency(Math.abs(costImpactData.summary.annual_savings_cents))}/year more`
                            : 'No annual savings impact'
                        }
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </TooltipProvider>
  );
};

export default ScenarioImpactBanner;
